import { env } from '@/config/env';

import { ApiError, toApiError } from './ApiError';

/**
 * Resolves a TikTok video link into its official embed, through
 * `supabase/functions/tiktok-oembed` — a thin server-side proxy for TikTok's
 * own oEmbed endpoint. Calling tiktok.com's oEmbed directly from the browser
 * does not work: verified by hand 2026-10-31, it returns clean JSON but sends
 * no `Access-Control-Allow-Origin` header, so `fetch()` is blocked by CORS.
 *
 * Nothing in this module runs until a caller calls `fetchTikTokOembed` —
 * loading this file does not reach tiktok.com. That is the click-to-load
 * facade's whole privacy argument: the component that calls this only mounts
 * after a visitor taps "Watch on TikTok".
 */

export interface TikTokOembed {
  /** TikTok's own embed markup — a single `<iframe>`, nothing else. */
  html: string;
  width: number;
  height: number;
}

interface RawTikTokOembed {
  html?: string;
  width?: string | number;
  height?: string | number;
  error?: string;
}

const GENERIC = "This clip couldn't be loaded.";
/** TikTok's own default player size, used when a response omits one. */
const DEFAULT_WIDTH = 325;
const DEFAULT_HEIGHT = 578;

export async function fetchTikTokOembed(tiktokUrl: string): Promise<TikTokOembed> {
  if (!env.supabaseUrl || !env.supabaseAnonKey) {
    throw new ApiError('unavailable', GENERIC);
  }

  try {
    const response = await fetch(
      `${env.supabaseUrl}/functions/v1/tiktok-oembed?url=${encodeURIComponent(tiktokUrl)}`,
      {
        headers: {
          apikey: env.supabaseAnonKey,
          Authorization: `Bearer ${env.supabaseAnonKey}`,
        },
      },
    );

    const json = (await response.json().catch(() => null)) as RawTikTokOembed | null;
    if (!response.ok || !json?.html) {
      throw new ApiError('unavailable', json?.error ?? GENERIC);
    }

    return {
      html: json.html,
      width: Number(json.width) || DEFAULT_WIDTH,
      height: Number(json.height) || DEFAULT_HEIGHT,
    };
  } catch (error) {
    throw toApiError(error);
  }
}
